/**
 * Document Editor Component
 * Rich text editor for editing generated documents with version history and AI assistance
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FileText,
  Save,
  Send,
  Download,
  History,
  Wand2,
  Lock,
  AlertCircle,
  CheckCircle,
  Loader,
  X,
  ChevronDown,
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Clock,
  User,
  Eye,
  FileDown,
  RotateCcw,
  Maximize2,
  Minimize2
} from 'lucide-react';
import { Card, CardHeader, CardBody, CardFooter } from '../../common/Card';
import { Button } from '../../common/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { documentService } from '../../../services/api/documentGeneratorService';
import { AIAssistantPanel } from './AIAssistantPanel';
import {
  GeneratedDocument,
  DocumentVersion,
  DOCUMENT_STATUS_LABELS,
  CONFIDENTIALITY_LABELS
} from '../../../types/documentGenerator.types';

interface DocumentEditorProps {
  document: GeneratedDocument;
  onUpdate?: (document: GeneratedDocument) => void;
  onClose?: () => void;
}

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  pending_review: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  pending_approval: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  pending_signature: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  signed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  expired: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  archived: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
};

export const DocumentEditor: React.FC<DocumentEditorProps> = ({
  document: initialDocument,
  onUpdate,
  onClose
}) => {
  const { token } = useAuth();
  const editorRef = useRef<HTMLDivElement>(null);

  // State
  const [document, setDocument] = useState<GeneratedDocument>(initialDocument);
  const [content, setContent] = useState(initialDocument.content);
  const [originalContent, setOriginalContent] = useState(initialDocument.content);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [changeDescription, setChangeDescription] = useState('');
  const [selectedVersion, setSelectedVersion] = useState<DocumentVersion | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Track content changes
  useEffect(() => {
    setHasChanges(content !== originalContent);
  }, [content, originalContent]);

  // Update document when prop changes
  useEffect(() => {
    setDocument(initialDocument);
    setContent(initialDocument.content);
    setOriginalContent(initialDocument.content);
    setHasChanges(false);
  }, [initialDocument]);

  // Save document
  const handleSave = useCallback(async () => {
    if (!token || !hasChanges) return;

    setSaving(true);
    setError(null);

    try {
      const updatedDoc = await documentService.update(token, document._id, {
        content,
        changeDescription: changeDescription || 'Content updated'
      });
      setDocument(updatedDoc);
      setOriginalContent(content);
      setHasChanges(false);
      setChangeDescription('');
      setSuccess('Document saved successfully');
      onUpdate?.(updatedDoc);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save document');
    } finally {
      setSaving(false);
    }
  }, [token, document._id, content, changeDescription, hasChanges, onUpdate]);

  // Submit for approval
  const handleSubmitForApproval = useCallback(async () => {
    if (!token) return;

    // Save first if there are changes
    if (hasChanges) {
      await handleSave();
    }

    setSubmitting(true);
    setError(null);

    try {
      const updatedDoc = await documentService.submitForApproval(token, document._id);
      setDocument(updatedDoc);
      setSuccess('Document submitted for approval');
      onUpdate?.(updatedDoc);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to submit for approval');
    } finally {
      setSubmitting(false);
    }
  }, [token, document._id, hasChanges, handleSave, onUpdate]);

  // Download PDF
  const handleDownloadPdf = useCallback(async () => {
    if (!token) return;

    try {
      const blob = await documentService.downloadPdf(token, document._id);
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `${document.title}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setShowExportMenu(false);
    } catch (err: any) {
      setError(err.message || 'Failed to download PDF');
    }
  }, [token, document._id, document.title]);

  // Download DOCX
  const handleDownloadDocx = useCallback(async () => {
    if (!token) return;

    try {
      const blob = await documentService.downloadDocx(token, document._id);
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `${document.title}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      setShowExportMenu(false);
    } catch (err: any) {
      setError(err.message || 'Failed to download DOCX');
    }
  }, [token, document._id, document.title]);

  // View version
  const handleViewVersion = useCallback(async (version: DocumentVersion) => {
    setSelectedVersion(version);
  }, []);

  // Revert to version
  const handleRevertToVersion = useCallback((version: DocumentVersion) => {
    setContent(version.content);
    setChangeDescription(`Reverted to version ${version.versionNumber}`);
    setSelectedVersion(null);
    setShowVersionHistory(false);
  }, []);

  // Apply AI improvement
  const handleAIImprove = useCallback((improvedContent: string) => {
    setContent(improvedContent);
    setChangeDescription('AI-assisted content improvement');
  }, []);

  // Toggle fullscreen mode
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  // Handle escape key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if document is editable
  const isEditable = document.status === 'draft' || document.status === 'rejected';

  // Simple text formatting (could be expanded with a proper rich text editor)
  const handleFormat = (format: string) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();
    if (!selectedText) return;

    let formattedText = selectedText;
    switch (format) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        break;
      case 'h1':
        formattedText = `\n# ${selectedText}\n`;
        break;
      case 'h2':
        formattedText = `\n## ${selectedText}\n`;
        break;
      case 'ul':
        formattedText = selectedText.split('\n').map(line => `- ${line}`).join('\n');
        break;
      case 'ol':
        formattedText = selectedText.split('\n').map((line, i) => `${i + 1}. ${line}`).join('\n');
        break;
    }

    // Replace the selection with formatted text
    const start = content.indexOf(selectedText);
    if (start !== -1) {
      const newContent = content.substring(0, start) + formattedText + content.substring(start + selectedText.length);
      setContent(newContent);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Document Info */}
      <Card variant="premium" padding="md">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-primary-100 dark:bg-primary-900/30">
              <FileText className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-xl font-semibold text-text-primary">{document.title}</h2>
                <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${statusColors[document.status]}`}>
                  {DOCUMENT_STATUS_LABELS[document.status]}
                </span>
              </div>
              <p className="text-sm text-text-tertiary">{document.documentNumber}</p>
              <div className="flex items-center gap-4 mt-2 text-sm text-text-secondary">
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {document.createdBy.firstName} {document.createdBy.lastName}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {formatDate(document.createdAt)}
                </span>
                <span className="flex items-center gap-1">
                  <Lock className="w-4 h-4" />
                  {CONFIDENTIALITY_LABELS[document.confidentialityLevel]}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onClose && (
              <Button variant="secondary" size="sm" onClick={onClose}>
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <p>{success}</p>
        </div>
      )}

      {/* Main Editor Area */}
      <div className="flex gap-6">
        {/* Editor */}
        <div className={`flex-1 transition-all duration-300 ${showAIPanel ? 'max-w-[calc(100%-400px)]' : ''}`}>
          <Card>
            {/* Toolbar */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-1">
                {isEditable && (
                  <>
                    <button
                      onClick={() => handleFormat('bold')}
                      className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="Bold"
                    >
                      <Bold className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleFormat('italic')}
                      className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="Italic"
                    >
                      <Italic className="w-4 h-4" />
                    </button>
                    <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1" />
                    <button
                      onClick={() => handleFormat('h1')}
                      className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="Heading 1"
                    >
                      <Heading1 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleFormat('h2')}
                      className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="Heading 2"
                    >
                      <Heading2 className="w-4 h-4" />
                    </button>
                    <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1" />
                    <button
                      onClick={() => handleFormat('ul')}
                      className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="Bullet List"
                    >
                      <List className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleFormat('ol')}
                      className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="Numbered List"
                    >
                      <ListOrdered className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                {hasChanges && (
                  <span className="text-xs text-amber-600 dark:text-amber-400">
                    Unsaved changes
                  </span>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowVersionHistory(!showVersionHistory)}
                  className="gap-1"
                >
                  <History className="w-4 h-4" />
                  History
                </Button>
                <div className="relative">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    className="gap-1"
                  >
                    <Download className="w-4 h-4" />
                    Export
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                  {showExportMenu && (
                    <div className="absolute right-0 mt-1 w-40 bg-surface-base rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                      <button
                        onClick={handleDownloadPdf}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
                      >
                        <FileDown className="w-4 h-4" />
                        Download PDF
                      </button>
                      <button
                        onClick={handleDownloadDocx}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
                      >
                        <FileDown className="w-4 h-4" />
                        Download DOCX
                      </button>
                    </div>
                  )}
                </div>
                {isEditable && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowAIPanel(!showAIPanel)}
                    className="gap-1"
                  >
                    <Wand2 className="w-4 h-4" />
                    AI Assist
                  </Button>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={toggleFullscreen}
                  className="gap-1"
                  title={isFullscreen ? 'Exit Fullscreen (Esc)' : 'Fullscreen Edit'}
                >
                  {isFullscreen ? (
                    <Minimize2 className="w-4 h-4" />
                  ) : (
                    <Maximize2 className="w-4 h-4" />
                  )}
                  {isFullscreen ? 'Exit' : 'Fullscreen'}
                </Button>
              </div>
            </div>

            {/* Editor Content */}
            <div className="p-6">
              {isEditable ? (
                <textarea
                  ref={editorRef as any}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="w-full min-h-[500px] p-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm leading-relaxed"
                  placeholder="Document content..."
                />
              ) : (
                <div className="prose dark:prose-invert max-w-none">
                  <div
                    className="min-h-[500px] p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg whitespace-pre-wrap font-mono text-sm leading-relaxed"
                  >
                    {content}
                  </div>
                </div>
              )}

              {/* Change Description (when editing) */}
              {isEditable && hasChanges && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Change Description (optional)
                  </label>
                  <input
                    type="text"
                    value={changeDescription}
                    onChange={(e) => setChangeDescription(e.target.value)}
                    placeholder="Briefly describe what you changed..."
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-surface-base text-text-primary focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-text-tertiary">
                Version {document.versions.length > 0 ? document.versions[document.versions.length - 1]?.versionNumber : 1}
                {document.lastModifiedBy && (
                  <> • Last modified by {document.lastModifiedBy.firstName} {document.lastModifiedBy.lastName}</>
                )}
              </div>
              <div className="flex items-center gap-3">
                {isEditable && (
                  <>
                    <Button
                      variant="secondary"
                      onClick={handleSave}
                      disabled={!hasChanges || saving}
                      className="gap-2"
                    >
                      {saving ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Save Draft
                    </Button>
                    <Button
                      onClick={handleSubmitForApproval}
                      disabled={submitting}
                      className="gap-2"
                    >
                      {submitting ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      Submit for Approval
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* AI Assistant Panel */}
        {showAIPanel && (
          <AIAssistantPanel
            document={document}
            content={content}
            onImprove={handleAIImprove}
            onClose={() => setShowAIPanel(false)}
          />
        )}
      </div>

      {/* Version History Modal */}
      {showVersionHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <CardHeader
              title="Version History"
              subtitle={`${document.versions.length} version${document.versions.length !== 1 ? 's' : ''}`}
              action={
                <Button variant="secondary" size="sm" onClick={() => setShowVersionHistory(false)}>
                  <X className="w-4 h-4" />
                </Button>
              }
            />
            <CardBody className="flex-1 overflow-auto">
              {document.versions.length === 0 ? (
                <p className="text-text-secondary text-center py-8">No version history available</p>
              ) : (
                <div className="space-y-3">
                  {[...document.versions].reverse().map((version) => (
                    <div
                      key={version.versionNumber}
                      className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-text-primary">
                              Version {version.versionNumber}
                            </span>
                            {version.changeDescription && (
                              <span className="text-sm text-text-secondary">
                                - {version.changeDescription}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-text-tertiary">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {version.createdBy.firstName} {version.createdBy.lastName}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(version.createdAt)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleViewVersion(version)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {isEditable && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleRevertToVersion(version)}
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      )}

      {/* Version Preview Modal */}
      {selectedVersion && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <CardHeader
              title={`Version ${selectedVersion.versionNumber}`}
              subtitle={selectedVersion.changeDescription || 'No description'}
              action={
                <Button variant="secondary" size="sm" onClick={() => setSelectedVersion(null)}>
                  <X className="w-4 h-4" />
                </Button>
              }
            />
            <CardBody className="flex-1 overflow-auto">
              <div className="prose dark:prose-invert max-w-none">
                <pre className="whitespace-pre-wrap font-mono text-sm bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                  {selectedVersion.content}
                </pre>
              </div>
            </CardBody>
            <CardFooter>
              <Button variant="secondary" onClick={() => setSelectedVersion(null)}>
                Close
              </Button>
              {isEditable && (
                <Button onClick={() => handleRevertToVersion(selectedVersion)}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Revert to This Version
                </Button>
              )}
            </CardFooter>
          </Card>
        </div>
      )}

      {/* Fullscreen Editor Modal */}
      {isFullscreen && (
        <div className="fixed inset-0 z-[70] bg-surface-base dark:bg-dark-bg flex flex-col">
          {/* Fullscreen Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-surface-base dark:bg-dark-surface">
            <div className="flex items-center gap-4">
              <FileText className="w-6 h-6 text-primary-600" />
              <div>
                <h2 className="text-lg font-semibold text-text-primary">{document.title}</h2>
                <p className="text-sm text-text-tertiary">Fullscreen Editor - Press Esc to exit</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {hasChanges && (
                <span className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  Unsaved changes
                </span>
              )}
              {isEditable && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleSave}
                  disabled={!hasChanges || saving}
                  className="gap-2"
                >
                  {saving ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={toggleFullscreen}
                className="gap-2"
              >
                <Minimize2 className="w-4 h-4" />
                Exit Fullscreen
              </Button>
            </div>
          </div>

          {/* Fullscreen Toolbar */}
          {isEditable && (
            <div className="flex items-center gap-1 px-6 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <button
                onClick={() => handleFormat('bold')}
                className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                title="Bold"
              >
                <Bold className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleFormat('italic')}
                className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                title="Italic"
              >
                <Italic className="w-4 h-4" />
              </button>
              <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-2" />
              <button
                onClick={() => handleFormat('h1')}
                className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                title="Heading 1"
              >
                <Heading1 className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleFormat('h2')}
                className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                title="Heading 2"
              >
                <Heading2 className="w-4 h-4" />
              </button>
              <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-2" />
              <button
                onClick={() => handleFormat('ul')}
                className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                title="Bullet List"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleFormat('ol')}
                className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                title="Numbered List"
              >
                <ListOrdered className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Fullscreen Editor Content */}
          <div className="flex-1 overflow-auto p-6">
            <div className="max-w-5xl mx-auto h-full">
              {isEditable ? (
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="w-full h-full min-h-[calc(100vh-200px)] p-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm leading-relaxed shadow-sm"
                  placeholder="Document content..."
                  autoFocus
                />
              ) : (
                <div className="prose dark:prose-invert max-w-none">
                  <div
                    className="min-h-[calc(100vh-200px)] p-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl whitespace-pre-wrap font-mono text-sm leading-relaxed shadow-sm"
                  >
                    {content}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Fullscreen Footer */}
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="text-sm text-text-tertiary">
              {content.length.toLocaleString()} characters • {content.split(/\s+/).filter(Boolean).length.toLocaleString()} words
            </div>
            {isEditable && hasChanges && (
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={changeDescription}
                  onChange={(e) => setChangeDescription(e.target.value)}
                  placeholder="Describe your changes..."
                  className="w-64 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-text-primary focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  size="sm"
                  className="gap-2"
                >
                  {saving ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save Changes
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentEditor;
